import { AdminHomebrew } from "./Lore.js";
import { useEffect, useState } from "react";
import type { Account, GlobalMemoryCategory, GlobalMemoryEntry, GlobalMemorySuggestion, OwedReport } from "@dm/shared";
import { api } from "./api.js";
import { useApp } from "./appContext.js";

/** Admin: every account, with "Can host" and "Admin" toggles. */
export function AdminPage() {
  const { me, fail, reloadMe } = useApp();
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  useEffect(() => {
    api.accounts().then((r) => setAccounts(r.accounts), fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!me.isAdmin) return <main className="muted">Admins only.</main>;
  if (!accounts) return <main className="muted">Loading…</main>;

  const flip = async (a: Account, flags: { isAdmin?: boolean; canHost?: boolean; canUseServerKey?: boolean }) => {
    if (flags.isAdmin === false && a.discordId === me.user.discordId && !window.confirm("Remove your own Admin rights?")) return;
    try {
      const updated = await api.setAccountFlags(a.discordId, flags);
      setAccounts((list) => list!.map((x) => (x.discordId === a.discordId ? updated : x)));
      if (a.discordId === me.user.discordId) await reloadMe();
    } catch (err) {
      fail(err);
    }
  };

  return (
    <main>
      <WhoOwesMe />
      <section className="panel">
        <h3>Accounts</h3>
        <p className="muted small">
          Anyone who logs in with Discord can play. Turn on <strong>Can host</strong> to let an account create campaigns. Admins can do anything and
          manage this list.
        </p>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Discord ID</th>
              <th>Joined</th>
              <th>Last login</th>
              <th>Can host</th>
              <th title="Campaigns this account hosts may run on the server's xAI key">Can use server key</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.discordId}>
                <td>
                  <span className="acct">
                    {a.avatarUrl ? <img src={a.avatarUrl} alt="" /> : <span className="avatar-blank" />}
                    <strong>{a.displayName}</strong> <span className="muted small">@{a.username}</span>
                  </span>
                </td>
                <td className="muted small">{a.discordId}</td>
                <td className="small">{new Date(a.createdAt).toLocaleDateString()}</td>
                <td className="small">{new Date(a.lastLoginAt).toLocaleString()}</td>
                <td>
                  <input type="checkbox" checked={a.canHost || a.isAdmin} disabled={a.isAdmin} title={a.isAdmin ? "Admins can always host" : undefined} onChange={() => flip(a, { canHost: !a.canHost })} />
                </td>
                <td>
                  <input type="checkbox" aria-label={`${a.displayName} can use server key`} checked={a.canUseServerKey || a.isAdmin} disabled={a.isAdmin}
                    title={a.isAdmin ? "Admins can always use the server key" : "Campaigns this account hosts may use the server's xAI key"} onChange={() => flip(a, { canUseServerKey: !a.canUseServerKey })} />
                </td>
                <td>
                  <input type="checkbox" checked={a.isAdmin} onChange={() => flip(a, { isAdmin: !a.isAdmin })} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <GlobalMemory />
      <AdminHomebrew />
    </main>
  );
}

const CATEGORY: Record<GlobalMemoryCategory, string> = { rule: "House rule", style: "Style", lesson: "Lesson" };

/** Global DM memory: house rules, style guidance and lessons every campaign's DM always follows; plus the Hosts' suggestions. */
function GlobalMemory() {
  const { fail } = useApp();
  const [entries, setEntries] = useState<GlobalMemoryEntry[] | null>(null);
  const [suggestions, setSuggestions] = useState<GlobalMemorySuggestion[]>([]);
  const [category, setCategory] = useState<GlobalMemoryCategory>("rule");
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<{ id: number; category: GlobalMemoryCategory; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.globalMemory().then((r) => {
      setEntries(r.entries);
      setSuggestions(r.suggestions);
    }, fail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  if (!entries) return null;
  const move = (i: number, d: -1 | 1) => {
    const ids = entries.map((e) => e.id);
    const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    void run(async () => setEntries((await api.reorderGlobal(ids)).entries));
  };
  const pending = suggestions.filter((s) => s.status === "pending");
  const decided = suggestions.filter((s) => s.status !== "pending").slice(0, 10);
  return (
    <>
      <section className="panel">
        <h3>Global DM memory</h3>
        <p className="muted small">
          Always given to the DM in every campaign, in this order. Use it for house rules, narration style and lessons learned.
        </p>
        <ol className="global-list">
          {entries.map((e, i) => (
            <li key={e.id}>
              {editing?.id === e.id ? (
                <div className="form-row">
                  <select value={editing.category} onChange={(ev) => setEditing({ ...editing, category: ev.target.value as GlobalMemoryCategory })}>
                    {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <input className="grow" value={editing.text} maxLength={1000} onChange={(ev) => setEditing({ ...editing, text: ev.target.value })} />
                  <button className="primary" disabled={busy || !editing.text.trim()} onClick={() => run(async () => { setEntries((await api.updateGlobal(e.id, editing.category, editing.text)).entries); setEditing(null); })}>Save</button>
                  <button onClick={() => setEditing(null)}>Cancel</button>
                </div>
              ) : (
                <div className="form-row">
                  <span className={`tag cat-${e.category}`}>{CATEGORY[e.category]}</span>
                  <span className="grow">{e.text}</span>
                  <button className="link" disabled={busy || i === 0} title="Move up" onClick={() => move(i, -1)}>▲</button>
                  <button className="link" disabled={busy || i === entries.length - 1} title="Move down" onClick={() => move(i, 1)}>▼</button>
                  <button className="link" onClick={() => setEditing({ id: e.id, category: e.category, text: e.text })}>Edit</button>
                  <button className="link danger" onClick={() => window.confirm("Delete this entry?") && run(async () => setEntries((await api.deleteGlobal(e.id)).entries))}>Delete</button>
                </div>
              )}
            </li>
          ))}
        </ol>
        {entries.length === 0 && <p className="muted">Nothing yet.</p>}
        <div className="form-row">
          <select value={category} onChange={(e) => setCategory(e.target.value as GlobalMemoryCategory)}>
            {Object.entries(CATEGORY).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="grow" value={text} maxLength={1000} placeholder="e.g. Natural 1s on attacks are just misses, no fumbles." onChange={(e) => setText(e.target.value)} />
          <button className="primary" disabled={busy || !text.trim()} onClick={() => run(async () => { setEntries((await api.addGlobal(category, text.trim())).entries); setText(""); })}>
            Add
          </button>
        </div>
      </section>
      <section className="panel">
        <h3>Suggestions from Hosts {pending.length > 0 && <span className="tag host">{pending.length} waiting</span>}</h3>
        {pending.length === 0 && <p className="muted">No suggestions waiting.</p>}
        <ul className="suggestions">
          {pending.map((s) => (
            <li key={s.id} className="form-row">
              <span className={`tag cat-${s.category}`}>{CATEGORY[s.category]}</span>
              <span className="grow">
                {s.text}
                <span className="muted small"> — {s.suggestedByName ?? "?"}{s.campaignName ? ` (${s.campaignName})` : ""}</span>
              </span>
              <button className="primary" disabled={busy} onClick={() => run(async () => { const r = await api.decideSuggestion(s.id, true); setEntries(r.entries); setSuggestions(r.suggestions); })}>Approve</button>
              <button disabled={busy} onClick={() => run(async () => { const r = await api.decideSuggestion(s.id, false); setEntries(r.entries); setSuggestions(r.suggestions); })}>Reject</button>
            </li>
          ))}
        </ul>
        {decided.length > 0 && (
          <details>
            <summary className="muted small">Recently decided</summary>
            <ul className="suggestions">
              {decided.map((s) => (
                <li key={s.id} className="muted small">
                  {s.status === "approved" ? "✓" : "✗"} {s.text} — {s.suggestedByName ?? "?"}
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </>
  );
}

const money = (n: number) => `$${n.toFixed(2)}`;

/**
 * Admin: the server-key margin and "Who owes me": players' shares of sessions run on the server xAI key,
 * split evenly across the players present (Admins aren't charged), with payments and a running balance.
 */
export function WhoOwesMe() {
  const { fail } = useApp();
  const [r, setR] = useState<OwedReport | null>(null);
  const [margin, setMargin] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [pay, setPay] = useState<Record<string, { amount: string; note: string }>>({});
  const load = () => api.owed().then((x) => { setR(x); setMargin(String(x.serverMarginPct)); }, fail);
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!r) return null;
  const saveMargin = () => api.setServerMargin(Number(margin)).then(load, fail);
  const record = (userId: string) => {
    const p = pay[userId];
    return api.addPayment(userId, Number(p?.amount), p?.note ?? "").then((x) => { setR(x); setPay({ ...pay, [userId]: { amount: "", note: "" } }); }, fail);
  };
  return (
    <section className="panel who-owes">
      <h3>Who owes me (server key)</h3>
      <div className="row">
        <label htmlFor="srv-margin">Server-key margin: </label>
        <input id="srv-margin" type="number" min={0} max={500} step={1} value={margin} onChange={(e) => setMargin(e.target.value)} style={{ width: "6em" }} /> %
        <button className="primary" disabled={Number(margin) === r.serverMarginPct} onClick={() => void saveMargin()}>Save</button>
      </div>
      <p className="muted small">
        Players see AI cost on the server key with this margin added (cost popup, Costs page), and their shares below include it. It replaces the
        Host's cost multiplier for server-key calls, so nothing is marked up twice. Ended sessions keep the margin they ended at.
        Each session's price is split evenly across the players who were there; Admins aren't charged.
      </p>
      <p className="small">Raw cost {money(r.totalActualUsd)} · owed {money(r.totalOwedUsd)} · paid {money(r.totalPaidUsd)} · <strong>balance {money(r.totalBalanceUsd)}</strong></p>
      {r.players.length === 0 ? <p className="muted small">No sessions on the server key yet.</p> : (
        <table className="simple">
          <thead><tr><th>Player</th><th>Owed</th><th>Paid</th><th>Balance</th><th /></tr></thead>
          <tbody>
            {r.players.map((p) => (
              <tr key={p.userId}>
                <td>
                  <button className="link" onClick={() => setOpen(open === p.userId ? null : p.userId)}>{p.name}</button>
                  {open === p.userId && (
                    <table className="simple small">
                      <thead><tr><th>Date</th><th>What</th><th>Amount</th><th>Balance</th></tr></thead>
                      <tbody>
                        {p.ledger.map((e, i) => (
                          <tr key={i}><td>{new Date(e.at).toLocaleDateString()}</td><td>{e.label}</td><td>{money(e.amountUsd)}</td><td>{money(e.balanceUsd)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {open === p.userId && p.sessions.length > 0 && (
                    <p className="muted small">Raw share {money(p.sessions.reduce((a, s) => a + s.actualShareUsd, 0))} of actual cost; the rest is margin.</p>
                  )}
                  {open === p.userId && p.payments.map((x) => (
                    <div key={x.id} className="muted small">{new Date(x.at).toLocaleDateString()} {x.kind === "settle" ? "settled" : "paid"} {money(x.amountUsd)}{x.note ? ` (${x.note})` : ""}
                      {" "}<button className="link" onClick={() => void api.deletePayment(x.id).then(setR, fail)}>undo</button></div>
                  ))}
                </td>
                <td>{money(p.owedUsd)}</td>
                <td>{money(p.paidUsd)}</td>
                <td><strong>{money(p.balanceUsd)}</strong></td>
                <td>
                  <input type="number" min={0} step={0.01} placeholder="$" style={{ width: "5em" }} value={pay[p.userId]?.amount ?? ""}
                    onChange={(e) => setPay({ ...pay, [p.userId]: { amount: e.target.value, note: pay[p.userId]?.note ?? "" } })} />
                  <input placeholder="note" style={{ width: "7em" }} value={pay[p.userId]?.note ?? ""}
                    onChange={(e) => setPay({ ...pay, [p.userId]: { amount: pay[p.userId]?.amount ?? "", note: e.target.value } })} />
                  <button disabled={!(Number(pay[p.userId]?.amount) > 0)} onClick={() => void record(p.userId)}>Record payment</button>
                  <button disabled={p.balanceUsd < 0.005} onClick={() => void api.settle(p.userId).then(setR, fail)}>Mark settled</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
